import convertOperator from 'cio/utils/operators/convert';
import memoize from 'lodash.memoize';

type Operator = {
  label: string;
  value: string;
  hasPredicate?: boolean;
  allowBlankPredicate?: boolean;
  allowDuplicate?: boolean;
  updateLabel?: string;
};

export const equalityOperators: Operator[] = [
  {
    label: 'is equal to',
    updateLabel: 'changes to be equal to',
    value: '=',
    hasPredicate: true,
    allowBlankPredicate: false,
  },
  {
    label: 'is not equal to',
    updateLabel: 'changes to not equal to',
    value: '!=',
    hasPredicate: true,
    allowDuplicate: true,
    allowBlankPredicate: false,
  },
];

export const inequalityOperators: Operator[] = [
  {
    label: 'is greater than',
    updateLabel: 'changes to be greater than',
    value: '>',
    hasPredicate: true,
  },
  {
    label: 'is less than',
    updateLabel: 'changes to be less than',
    value: '<',
    hasPredicate: true,
  },
  {
    label: 'is between',
    updateLabel: 'changes to be between',
    value: 'bw',
    hasPredicate: true,
  },
];

export const existenceOperators: Operator[] = [
  {
    label: 'exists',
    updateLabel: 'changes and any value exists',
    value: 'exists',
    hasPredicate: false,
  },
  {
    label: 'does not exist',
    updateLabel: 'changes and no value exists',
    value: '!exists',
    hasPredicate: false,
  },
];

export const stringOperators: Operator[] = [
  {
    label: 'contains',
    updateLabel: 'changes to contain',
    value: 'contains',
    hasPredicate: true,
  },
  {
    label: 'does not contain',
    updateLabel: 'changes to no longer contain',
    value: '!contains',
    hasPredicate: true,
  },
];

export const dateExistenceOperators: Operator[] = [
  {
    label: 'is a timestamp',
    updateLabel: 'changes to be a timestamp',
    value: 'timestamp',
    hasPredicate: false,
  },
  {
    label: 'is not a timestamp',
    updateLabel: 'changes to be not a timestamp',
    value: '!timestamp',
    hasPredicate: false,
  },
];

export const dateInequalityOperators: Operator[] = [
  {
    label: 'is a timestamp after',
    updateLabel: 'changes to be a timestamp after',
    value: 'timestamp_gt',
    hasPredicate: true,
  },
  {
    label: 'is a timestamp before',
    updateLabel: 'changes to be a timestamp before',
    value: 'timestamp_lt',
    hasPredicate: true,
  },

  // absolute timestamp operators: with same labels and formatting, not displayed in operator select input when editing attributes
  {
    label: 'is a timestamp after',
    updateLabel: 'changes to be a timestamp after',
    value: 'timestamp_gta',
    hasPredicate: true,
  },
  {
    label: 'is a timestamp before',
    updateLabel: 'changes to be a timestamp before',
    value: 'timestamp_lta',
    hasPredicate: true,
  },
];

export const extraDateInequalityOperators: Operator[] = [
  {
    label: 'is a timestamp between',
    updateLabel: 'changes to be a timestamp between',
    value: 'timestamp_bw',
    hasPredicate: true,
  },
];

export const emailOperators: Operator[] = [
  {
    label: 'is valid',
    updateLabel: 'changes to be valid',
    value: 'email_valid',
    hasPredicate: false,
  },
  {
    label: 'is not valid',
    updateLabel: 'changes to be not valid',
    value: '!email_valid',
    hasPredicate: false,
  },
];

export const arrayOperators: Operator[] = [
  {
    label: 'is contained in',
    updateLabel: 'changes and is contained in',
    value: 'contained_in_array',
    hasPredicate: true,
  },
  {
    label: 'is not contained in',
    updateLabel: 'changes and no longer is contained in',
    value: '!contained_in_array',
    hasPredicate: true,
  },
];

export const dateOperators = [...dateExistenceOperators, ...dateInequalityOperators, ...extraDateInequalityOperators];

export const limitedOperators = [...equalityOperators, ...existenceOperators];
export const allOperators = [
  ...equalityOperators,
  ...inequalityOperators,
  ...existenceOperators,
  ...stringOperators,
  ...dateOperators,
  ...emailOperators,
  ...arrayOperators,
];

function _findOperator(operator: string) {
  return allOperators.findBy('value', convertOperator(operator, { to: 'attribute' }));
}

export const findOperator = memoize(_findOperator);
